import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Receipt, Upload, Camera, FileText, Image as ImgIcon, ExternalLink,
  Trash2, AlertCircle, Loader2, RefreshCcw, Filter,
} from "lucide-react";

const formatBytes = (bytes) => {
  if (!bytes) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const monthLabel = (year, month) => {
  const d = new Date(year, month - 1, 1);
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

const currency = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Receipts() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [list, setList] = useState({ receipts: [], count: 0, total_amount: 0 });
  const [listLoading, setListLoading] = useState(true);
  const [monthFilter, setMonthFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [scope, setScope] = useState("mine"); // admin only: mine | all

  // Upload form state
  const [file, setFile] = useState(null);
  const [customName, setCustomName] = useState("");
  const [category, setCategory] = useState("General");
  const [notes, setNotes] = useState("");
  const [amount, setAmount] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const cameraRef = useRef(null);

  const [confirmDelete, setConfirmDelete] = useState(null); // receipt object

  const loadStatus = async () => {
    setStatusLoading(true);
    try {
      const { data } = await api.get("/receipts/storage/status");
      setStatus(data);
      setCategories(data.categories || []);
    } catch (e) {
      setStatus({ connected: false });
    } finally {
      setStatusLoading(false);
    }
  };

  const loadList = async () => {
    setListLoading(true);
    try {
      const params = {};
      if (monthFilter) params.month = monthFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (isAdmin && scope === "mine") params.user_id = user.user_id;
      const { data } = await api.get("/receipts", { params });
      setList(data);
    } catch (e) {
      toast.error("Couldn't load receipts");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [monthFilter, categoryFilter, scope, isAdmin]);

  const handlePickFile = (f) => {
    if (!f) return;
    const validTypes = ["image/", "application/pdf"];
    const ok = validTypes.some((t) => f.type.startsWith(t)) ||
      /\.(pdf|jpe?g|png|heic|heif|webp|gif)$/i.test(f.name);
    if (!ok) {
      toast.error("Use a PDF or image (JPG/PNG/HEIC/WEBP)");
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error("File exceeds 25 MB limit");
      return;
    }
    setFile(f);
    if (!customName) {
      // Pre-fill the custom name with the original filename minus extension
      const dot = f.name.lastIndexOf(".");
      setCustomName(dot > 0 ? f.name.slice(0, dot) : f.name);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    handlePickFile(f);
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!file) {
      toast.error("Pick a receipt file first");
      return;
    }
    if (!status?.connected) {
      toast.error("Google Drive isn't connected. Ask an admin to connect it in Settings.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (customName.trim()) fd.append("custom_filename", customName.trim());
      fd.append("category", category);
      if (notes.trim()) fd.append("notes", notes.trim());
      if (amount.trim()) fd.append("amount", amount.trim());
      const { data } = await api.post("/receipts/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Receipt uploaded to Google Drive");
      // Reset form
      setFile(null);
      setCustomName("");
      setCategory("General");
      setNotes("");
      setAmount("");
      if (inputRef.current) inputRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
      // Optimistically prepend the new row
      setList((prev) => ({
        ...prev,
        receipts: [data.receipt, ...(prev.receipts || [])],
        count: (prev.count || 0) + 1,
        total_amount: (prev.total_amount || 0) + (data.receipt.amount || 0),
      }));
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (receipt) => {
    try {
      await api.delete(`/receipts/${receipt.id}`);
      toast.success("Receipt deleted");
      setList((prev) => ({
        ...prev,
        receipts: (prev.receipts || []).filter((r) => r.id !== receipt.id),
        count: Math.max(0, (prev.count || 0) - 1),
        total_amount: Math.max(0, (prev.total_amount || 0) - (receipt.amount || 0)),
      }));
    } catch (err) {
      const msg = err?.response?.data?.detail || "Delete failed";
      toast.error(msg);
    } finally {
      setConfirmDelete(null);
    }
  };

  const grouped = useMemo(() => {
    const buckets = {};
    for (const r of list.receipts || []) {
      const key = `${r.year}-${String(r.month).padStart(2, "0")}`;
      buckets[key] = buckets[key] || { key, year: r.year, month: r.month, items: [], total: 0 };
      buckets[key].items.push(r);
      buckets[key].total += Number(r.amount || 0);
    }
    return Object.values(buckets).sort((a, b) => b.key.localeCompare(a.key));
  }, [list.receipts]);

  const driveScopeMissing = status && status.connected && status.has_drive_scope === false;
  const driveLegacy = status && status.connected && status.has_drive_scope === null;
  const parentMissing = status && status.connected && status.has_drive_scope === true && !status.parent_folder_id;

  return (
    <div className="space-y-8" data-testid="receipts-dashboard">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-semibold">Receipts</p>
        <h2 className="font-heading text-3xl font-semibold text-brand-primary mt-1">
          Drop a receipt, we'll file it
        </h2>
        <p className="text-sm text-brand-primary/60 mt-1">
          {status?.parent_folder_name ? (
            <>
              Receipts are stored in Google Drive under{" "}
              <code className="font-mono text-xs">{status.parent_folder_name} / Receipts / {(user?.name || "Your Name").split(" ")[0]} / {new Date().getFullYear()} / {new Date().toLocaleDateString(undefined, { month: "long" })}</code>
              {status.parent_folder_link && (
                <a
                  href={status.parent_folder_link}
                  target="_blank"
                  rel="noreferrer"
                  className="text-terracotta hover:underline ml-2 inline-flex items-center gap-1"
                  data-testid="receipts-parent-folder-link"
                >
                  Open in Drive <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </>
          ) : (
            <>Receipts are stored in a shared Google Drive folder configured by an admin.</>
          )}
        </p>
      </div>

      {/* Connection banners */}
      {!statusLoading && !status?.connected && (
        <Card data-testid="receipts-not-connected" className="rounded-2xl bg-peach/20 border border-terracotta/20 shadow-warm p-5 flex items-start gap-4">
          <AlertCircle className="h-5 w-5 text-terracotta mt-0.5" strokeWidth={1.8} />
          <div className="flex-1">
            <p className="font-medium text-brand-primary">Google Drive isn't connected</p>
            <p className="text-sm text-brand-primary/70 mt-1">
              An admin needs to connect Google in{" "}
              <a href="/settings" className="text-terracotta hover:underline">Settings → Integrations</a>{" "}
              before receipts can be uploaded. The same connection powers Calendar.
            </p>
          </div>
        </Card>
      )}
      {!statusLoading && (driveScopeMissing || driveLegacy) && (
        <Card data-testid="receipts-reconnect-banner" className="rounded-2xl bg-gold/15 border border-gold/30 shadow-warm p-5 flex items-start gap-4">
          <AlertCircle className="h-5 w-5 text-gold mt-0.5" strokeWidth={1.8} />
          <div className="flex-1">
            <p className="font-medium text-brand-primary">
              {driveScopeMissing ? "Google Drive permission missing" : "Reconnect Google to enable Drive uploads"}
            </p>
            {driveScopeMissing ? (
              <div className="text-sm text-brand-primary/75 mt-1 space-y-2">
                <p>
                  You reconnected Google but the consent screen didn't ask for Drive access. This means the
                  <code className="font-mono text-xs mx-1">drive.file</code>
                  scope hasn't been added to your Google Cloud project's OAuth consent screen.
                </p>
                {isAdmin && (
                  <ol className="list-decimal pl-5 space-y-1 text-sm">
                    <li>
                      Open{" "}
                      <a
                        href="https://console.cloud.google.com/apis/credentials/consent"
                        target="_blank"
                        rel="noreferrer"
                        className="text-terracotta underline"
                      >
                        Google Cloud Console → OAuth consent screen
                      </a>
                      .
                    </li>
                    <li>Click <strong>Edit App</strong> → <strong>Scopes</strong> → <strong>Add or Remove Scopes</strong>.</li>
                    <li>
                      Add{" "}
                      <code className="font-mono text-xs">.../auth/drive</code>
                      {" "}(full Drive access — required so the dashboard can write into your existing shared staff folder).
                    </li>
                    <li><strong>Save</strong>, then come back to <a href="/settings" className="text-terracotta underline">Settings</a> and click Reconnect Google again.</li>
                  </ol>
                )}
                <p className="text-xs text-brand-primary/55 pt-1">
                  Currently granted: <code className="font-mono">{status?.granted_scopes || "—"}</code>
                </p>
              </div>
            ) : (
              <p className="text-sm text-brand-primary/70 mt-1">
                The current Google connection (
                <code className="font-mono text-xs">{status?.connected_email || "unknown"}</code>
                ) was authorized before Receipt uploads were added.{" "}
                {isAdmin
                  ? <>Head to <a href="/settings" className="text-terracotta hover:underline">Settings → Integrations</a> and click <em>Reconnect Google</em> to grant Drive access.</>
                  : <>Ask an admin to reconnect Google in Settings to grant Drive access.</>}
              </p>
            )}
          </div>
        </Card>
      )}

      {!statusLoading && parentMissing && (
        <Card data-testid="receipts-parent-missing-banner" className="rounded-2xl bg-gold/15 border border-gold/30 shadow-warm p-5 flex items-start gap-4">
          <AlertCircle className="h-5 w-5 text-gold mt-0.5" strokeWidth={1.8} />
          <div className="flex-1">
            <p className="font-medium text-brand-primary">Receipts folder not configured</p>
            <p className="text-sm text-brand-primary/70 mt-1">
              {isAdmin ? (
                <>
                  Paste the shared <em>Family Chapel Shared Staff</em> folder URL (or ID) into{" "}
                  <a href="/settings" className="text-terracotta hover:underline">Settings → Integrations → Receipts storage</a>{" "}
                  so receipts know where to land.
                </>
              ) : (
                <>Ask an admin to set the receipts folder in Settings before uploading.</>
              )}
            </p>
          </div>
        </Card>
      )}

      {/* Upload card */}
      <form onSubmit={onSubmit} data-testid="receipt-upload-form">
        <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Drop zone */}
            <div
              data-testid="receipt-drop-zone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              className={`flex flex-col items-center justify-center text-center rounded-xl border-2 border-dashed cursor-pointer p-8 transition-colors ${
                dragOver ? "border-terracotta bg-terracotta/5" : "border-brand-primary/20 bg-brand-primary/[0.02]"
              }`}
            >
              <input
                ref={inputRef}
                type="file"
                accept="image/*,application/pdf,.heic,.heif"
                className="hidden"
                onChange={(e) => handlePickFile(e.target.files?.[0])}
                data-testid="receipt-file-input"
              />
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handlePickFile(e.target.files?.[0])}
                data-testid="receipt-camera-input"
              />
              {file ? (
                <>
                  {file.type.startsWith("image/") ? (
                    <ImgIcon className="h-10 w-10 text-terracotta mb-3" strokeWidth={1.5} />
                  ) : (
                    <FileText className="h-10 w-10 text-terracotta mb-3" strokeWidth={1.5} />
                  )}
                  <p className="font-medium text-brand-primary truncate max-w-full" data-testid="receipt-selected-filename">
                    {file.name}
                  </p>
                  <p className="text-xs text-brand-primary/55 mt-1">{formatBytes(file.size)} · {file.type || "unknown"}</p>
                  <p className="text-xs text-terracotta mt-3 underline">Choose a different file</p>
                </>
              ) : (
                <>
                  <Upload className="h-10 w-10 text-brand-primary/45 mb-3" strokeWidth={1.5} />
                  <p className="font-medium text-brand-primary">Drag a receipt here, or tap to browse</p>
                  <p className="text-xs text-brand-primary/55 mt-1">PDF or image · up to 25 MB</p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={(e) => { e.stopPropagation(); cameraRef.current?.click(); }}
                    className="mt-4 inline-flex items-center gap-2"
                    data-testid="receipt-camera-button"
                  >
                    <Camera className="h-4 w-4" /> Take photo with camera
                  </Button>
                </>
              )}
            </div>

            {/* Form fields */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="receipt-name">Filename (optional)</Label>
                <Input
                  id="receipt-name"
                  data-testid="receipt-custom-filename"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="e.g. Lunch with worship team"
                  className="mt-1"
                />
                <p className="text-xs text-brand-primary/50 mt-1">
                  We append a UTC timestamp + the original extension automatically.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="receipt-category">Category</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger id="receipt-category" className="mt-1" data-testid="receipt-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {(categories.length ? categories : ["General"]).map((c) => (
                        <SelectItem key={c} value={c} data-testid={`receipt-category-option-${c}`}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="receipt-amount">Amount (optional)</Label>
                  <Input
                    id="receipt-amount"
                    data-testid="receipt-amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="$0.00"
                    inputMode="decimal"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="receipt-notes">Notes (optional)</Label>
                <Textarea
                  id="receipt-notes"
                  data-testid="receipt-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Vendor, attendees, project, etc."
                  rows={3}
                  className="mt-1"
                />
              </div>

              <Button
                type="submit"
                disabled={uploading || !file || !status?.connected || parentMissing}
                data-testid="receipt-upload-submit"
                className="w-full bg-terracotta hover:bg-terracotta-hover text-white h-11"
              >
                {uploading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading…</>
                ) : (
                  <><Upload className="h-4 w-4 mr-2" /> Upload receipt</>
                )}
              </Button>
            </div>
          </div>
        </Card>
      </form>

      {/* Filters + list */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-4 w-4 text-brand-primary/55" />
          <Input
            type="month"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="h-9 w-[160px]"
            data-testid="receipt-filter-month"
            placeholder="Filter month"
          />
          <Select value={categoryFilter || "__all__"} onValueChange={(v) => setCategoryFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-9 w-[200px]" data-testid="receipt-filter-category">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isAdmin && (
            <div className="inline-flex rounded-full bg-white border border-brand-primary/15 p-1" data-testid="receipt-scope-toggle">
              {[
                { k: "mine", label: "My receipts" },
                { k: "all", label: "All staff" },
              ].map((opt) => (
                <button
                  key={opt.k}
                  type="button"
                  onClick={() => setScope(opt.k)}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${
                    scope === opt.k ? "bg-brand-primary text-white" : "text-brand-primary/70 hover:text-brand-primary"
                  }`}
                  data-testid={`receipt-scope-${opt.k}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <p className="text-sm text-brand-primary/65" data-testid="receipt-summary">
            {list.count} {list.count === 1 ? "receipt" : "receipts"}
            {list.total_amount > 0 && <> · {currency(list.total_amount)} total</>}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={loadList}
            disabled={listLoading}
            data-testid="receipt-refresh-btn"
            className="h-9"
          >
            <RefreshCcw className={`h-4 w-4 mr-1.5 ${listLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {listLoading && list.receipts.length === 0 ? (
        <Card className="p-12 text-center text-brand-primary/55">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        </Card>
      ) : list.receipts.length === 0 ? (
        <Card className="p-12 text-center text-brand-primary/55" data-testid="receipts-empty-state">
          <Receipt className="h-10 w-10 mx-auto mb-3 text-brand-primary/30" strokeWidth={1.5} />
          <p className="font-medium text-brand-primary">No receipts yet</p>
          <p className="text-sm mt-1">Upload one above and it'll appear here.</p>
        </Card>
      ) : (
        <div className="space-y-6" data-testid="receipts-list">
          {grouped.map((g) => (
            <div key={g.key}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-lg text-brand-primary">{monthLabel(g.year, g.month)}</h3>
                <p className="text-xs text-brand-primary/55">
                  {g.items.length} {g.items.length === 1 ? "receipt" : "receipts"}
                  {g.total > 0 && <> · {currency(g.total)}</>}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {g.items.map((r) => {
                  const Icon = r.mime_type?.startsWith("image/") ? ImgIcon : FileText;
                  return (
                    <Card
                      key={r.id}
                      data-testid={`receipt-card-${r.id}`}
                      className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-5 flex flex-col gap-3"
                    >
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-xl bg-terracotta/10 grid place-items-center shrink-0">
                          <Icon className="h-5 w-5 text-terracotta" strokeWidth={1.6} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-brand-primary truncate" title={r.filename}>
                            {r.filename}
                          </p>
                          <p className="text-xs text-brand-primary/55 mt-0.5">
                            {r.category} · {formatBytes(r.size_bytes)}
                          </p>
                        </div>
                      </div>
                      {(r.amount != null || r.notes) && (
                        <div className="text-sm space-y-1">
                          {r.amount != null && (
                            <p className="font-medium text-brand-primary">{currency(r.amount)}</p>
                          )}
                          {r.notes && (
                            <p className="text-brand-primary/70 line-clamp-2">{r.notes}</p>
                          )}
                        </div>
                      )}
                      <div className="flex items-center justify-between text-xs text-brand-primary/55 mt-auto pt-2 border-t border-brand-primary/5">
                        <div className="min-w-0 truncate">
                          <span className="text-brand-primary/85 font-medium">{r.uploader_name}</span>
                          <span> · {new Date(r.uploaded_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {r.drive_view_link && (
                            <a
                              href={r.drive_view_link}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`receipt-open-${r.id}`}
                              className="inline-flex items-center gap-1 text-terracotta hover:underline"
                            >
                              Open <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                          {(isAdmin || r.uploader_user_id === user?.user_id) && (
                            <button
                              type="button"
                              onClick={() => setConfirmDelete(r)}
                              data-testid={`receipt-delete-${r.id}`}
                              className="inline-flex items-center text-brand-primary/55 hover:text-terracotta"
                              aria-label="Delete receipt"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="receipt-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete receipt?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDelete?.filename}" will be removed from Google Drive and the dashboard. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="receipt-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onDelete(confirmDelete)}
              data-testid="receipt-delete-confirm"
              className="bg-terracotta hover:bg-terracotta-hover"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
